// Port of api/fuel/app/classes/filter/core/filter.php
// Base class for all filter types (Collection, Plays, Ratings).
// Subclasses set _filter_type, _cache_type, _result_type, _pseudo_type, _package_types.

import F from 'offline/filter/constants'
import Template from 'offline/filter/core/template'
import Search from 'offline/filter/core/search'
import Package from 'offline/filter/core/package'
import Data from 'offline/filter/core/data'
import DB from 'offline/db'
import Util from 'offline/util'

function Filter(config) {
	this._config = config
	this._filter = null
	this._search = null
	this._package = []
	this._data = {}
}

Filter.prototype.run = function() {
	var config = this._config
	var profiling = DB.isProfiling()

	config.ownerType = config.owner.get_type_constant()
	config.filterType = this._filter_type
	config.cacheType = this._cache_type
	config.resultType = this._result_type
	config.pseudoType = this._pseudo_type
	config.packageType = this._package_types
	if (!config.asIndex) config.asIndex = false
	if (!config.filterMode) config.filterMode = F.MODE_STANDARD

	// Generate complete filter from template
	if (profiling) DB.phaseStart('template')
	this._filter = this.template(config.filter)
	if (profiling) DB.phaseEnd()

	// Allow subclass to modify config based on filter
	config = this.configure(config, this._filter)

	// Build user index
	if (config.ownerType === F.OWNER_GROUP) {
		var gLinks = DB.query('SELECT user_id FROM link_group_user WHERE group_id = ?', [config.owner.get_id()])
		config.users = Util.pluck(gLinks, 'user_id')
		if (this._filter.users && this._filter.users.length) {
			var ff = this._filter
			config.users = config.users.filter(function(u) { return ff.users.indexOf(u) !== -1 })
		}
	} else if (config.ownerType === F.OWNER_BUDDIES) {
		var bLinks = DB.query('SELECT user_id FROM link_buddies_user WHERE buddies_id = ?', [config.owner.get_id()])
		config.users = Util.pluck(bLinks, 'user_id')
		if (this._filter.users && this._filter.users.length) {
			var ff2 = this._filter
			config.users = config.users.filter(function(u) { return ff2.users.indexOf(u) !== -1 })
		}
	} else {
		config.users = [config.owner.get_id()]
	}

	if (config.ownerType === F.OWNER_USER) {
		config.userID = config.owner.get_id()
	} else if (config.ownerType === F.OWNER_BUDDIES) {
		config.userID = config.owner.get_user_id ? config.owner.get_user_id() : null
	} else {
		config.userID = config.sessionID || null
	}

	config.settings = config.settings || { session: {}, owner: {} }
	if (!config.can) config.can = {}
	if (!config.perpage) config.perpage = F.PERPAGE

	// Run the search
	if (profiling) DB.phaseStart('search')
	var searchData = {
		total: 0,
		pseudo: 0,
		maximum: 0,
		games: [],
		nested: [],
		users: config.users,
		results: []
	}

	searchData = this.search(searchData)
	if (profiling) DB.phaseEnd()

	this._search = {
		total: searchData.total,
		pseudo: searchData.pseudo,
		maximum: searchData.maximum || 0,
		results: searchData.results,
		games: searchData.games,
		nested: searchData.nested || [],
		users: searchData.users,
		mapIndex: searchData.mapIndex || {},
		mapPseudo: searchData.mapPseudo || {},
		key: 'offline_' + Date.now()
	}

	if (config.asIndex) {
		return this._search.results
	}

	// Paginate
	var total = this._search.total
	var perpage = config.perpage
	var page = config.page || 1
	var pages = Math.ceil(total / perpage) || 1

	var start = (page - 1) * perpage
	var end = Math.min(start + perpage, total)
	var pageResults = this._search.results.slice(start, end)

	// Package the results
	if (profiling) DB.phaseStart('package')
	if (total) {
		var packaged = this.package({
			results: pageResults,
			users: this._search.users,
			reference: this._search.results,
			nested: this._search.nested,
			context: {},
			mapIndex: this._search.mapIndex,
			mapPseudo: this._search.mapPseudo
		})

		this._package = packaged[0] || []
		this._data = packaged[1] || {}
	} else {
		this._data = {
			total: 0,
			pseudo: 0,
			games: [],
			nested: [],
			users: config.users,
			reference: [],
			results: []
		}
	}
	if (profiling) DB.phaseEnd()

	// Fetch reference data
	if (profiling) DB.phaseStart('data')
	this._data = this.data(this._data)
	if (profiling) DB.phaseEnd()

	return this
}

Filter.prototype.template = function(data) {
	return Template.run(this._config, this._required_filter || [], data)
}

Filter.prototype.configure = function(config) {
	return config
}

Filter.prototype.search = function(data) {
	return Search.run(this._config, this._filter, data)
}

Filter.prototype.package = function(data) {
	return Package.run(this._config, this._filter, data)
}

Filter.prototype.data = function(data) {
	return Data.run(this._config, this._filter, this._package, data || {})
}

Filter.prototype.to_array = function(extra) {
	var total = this._search ? this._search.total : 0
	var perpage = this._config.perpage || F.PERPAGE
	var totalMax = this._search && this._search.maximum !== undefined
		? this._search.maximum
		: total
	var result = {
		total: total,
		totalPseudo: this._search ? this._search.pseudo : 0,
		totalMax: totalMax,
		totalNested: this._search && this._search.nested ? this._search.nested.length : 0,
		key: this._search ? this._search.key : '',
		page: this._config.page || 1,
		toPage: this._config.toPage || false,
		pages: Math.ceil(total / perpage),
		data: this._data
	}

	result[this._json_package] = this._package

	if (extra) {
		var keys = Object.keys(extra)
		for (var i = 0; i < keys.length; i++) result[keys[i]] = extra[keys[i]]
	}

	return result
}

// Create a filter subclass
Filter.extend = function(props) {
	function SubFilter(config) {
		Filter.call(this, config)
		this._filter_type = props.filter_type
		this._cache_type = props.cache_type
		this._result_type = props.result_type
		this._pseudo_type = props.pseudo_type
		this._json_package = props.json_package || 'package'
		this._package_types = props.package_types || []
		this._required_filter = props.required_filter || []
	}
	SubFilter.prototype = Object.create(Filter.prototype)
	SubFilter.prototype.constructor = SubFilter
	if (props.configure) SubFilter.prototype.configure = props.configure
	if (props.search) SubFilter.prototype.search = props.search
	if (props.package) SubFilter.prototype.package = props.package
	if (props.data) SubFilter.prototype.data = props.data
	if (props.template) SubFilter.prototype.template = props.template

	SubFilter.forge = function(config) { return new SubFilter(config) }
	return SubFilter
}

export default Filter
